<script setup>
import { computed, nextTick, ref, watch } from 'vue';
import { useUISettings } from 'dashboard/composables/useUISettings';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import SidebarActionsHeader from 'dashboard/components-next/SidebarActionsHeader.vue';
import CopilotInput from './CopilotInput.vue';
import LuraCopilotSteps from './LuraCopilotSteps.vue';
import LuraCopilotApproval from './LuraCopilotApproval.vue';
import LuraCopilotAssistantMessage from './LuraCopilotAssistantMessage.vue';

const props = defineProps({
  thread: { type: Object, default: null },
  activeRun: { type: Object, default: null },
  conversationId: { type: Number, default: null },
  conversationInboxType: { type: String, default: '' },
});

const emit = defineEmits([
  'sendMessage',
  'decide',
  'cancel',
  'reset',
  'setAutoApprove',
]);

const { updateUISettings } = useUISettings();
const chatContainer = ref(null);

const runs = computed(() => props.thread?.runs || []);
const autoApprove = computed(() => !!props.thread?.autoApprove);
const hasRuns = computed(() => runs.value.length > 0);
const isBusy = computed(() => !!props.activeRun);

const SUGGESTIONS = [
  'Analisa o problema desse cliente',
  'Veja por que o servidor caiu',
  'Confere os logs do servidor',
  'Resolve o problema desse cliente',
];

const scrollToBottom = async () => {
  await nextTick();
  if (chatContainer.value) {
    chatContainer.value.scrollTop = chatContainer.value.scrollHeight;
  }
};

watch(
  () => props.thread,
  () => scrollToBottom(),
  { deep: true }
);

const closeCopilotPanel = () => {
  updateUISettings({
    is_copilot_panel_open: false,
    is_contact_sidebar_open: false,
  });
};

const headerButtons = computed(() => {
  const buttons = [];
  if (isBusy.value) {
    buttons.push({
      key: 'cancel',
      icon: 'i-lucide-square',
      tooltip: 'Cancelar execução',
    });
  }
  if (hasRuns.value) {
    buttons.push({
      key: 'reset',
      icon: 'i-lucide-refresh-ccw',
      tooltip: 'Limpar conversa com o Helix',
    });
  }
  return buttons;
});

const handleHeaderAction = key => {
  if (key === 'reset') emit('reset');
  if (key === 'cancel' && props.activeRun) emit('cancel', props.activeRun.id);
};

const pendingApprovals = run =>
  run.steps.filter(step => step.kind === 'approval' && !step.decision);

const runStatusLabel = run => {
  if (run.status === 'awaiting_approval') return 'Aguardando sua autorização';
  return '';
};
</script>

<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<template>
  <div class="flex flex-col h-full text-sm leading-6 tracking-tight w-full">
    <SidebarActionsHeader
      title="Helix"
      :buttons="headerButtons"
      @click="handleHeaderAction"
      @close="closeCopilotPanel"
    />

    <div
      ref="chatContainer"
      class="flex-1 flex px-4 py-4 overflow-y-auto items-start"
    >
      <div v-if="!conversationId" class="text-n-slate-11">
        Abra uma conversa para usar o Copilot.
      </div>

      <div v-else-if="hasRuns" class="space-y-6 flex-1 flex flex-col w-full">
        <template v-for="run in runs" :key="run.id">
          <div class="space-y-1 text-n-slate-12">
            <div class="font-medium flex items-center gap-2">
              <span>{{ run.agentName || 'Atendente' }}</span>
              <span
                v-if="run.autoApprove"
                v-tooltip.top="'Auto-aprovar estava ligado nesta execução'"
                class="inline-flex items-center gap-1 px-1.5 h-4 rounded text-[10px] font-medium uppercase tracking-wide bg-n-amber-3 text-n-amber-11"
              >
                <Icon icon="i-lucide-shield-check" class="w-3 h-3" />
                auto
              </span>
            </div>
            <div class="break-words">{{ run.input }}</div>
          </div>

          <LuraCopilotSteps
            v-if="run.steps.length"
            :steps="run.steps"
            :default-collapsed="run.status === 'completed'"
          />

          <LuraCopilotApproval
            v-for="approval in pendingApprovals(run)"
            :key="approval.callId"
            :approval="approval"
            @decide="
              decision =>
                emit('decide', {
                  runId: run.id,
                  callId: approval.callId,
                  ...decision,
                })
            "
          />

          <LuraCopilotAssistantMessage
            v-if="run.finalText || run.streamingText"
            :content="run.finalText || run.streamingText"
            :streaming="!run.finalText"
            :conversation-inbox-type="conversationInboxType"
          />

          <div
            v-if="run.status === 'failed' || run.status === 'cancelled'"
            class="flex items-start gap-2 text-n-ruby-11"
          >
            <Icon icon="i-lucide-circle-x" class="w-4 h-4 mt-1 flex-shrink-0" />
            <span>{{ run.error }}</span>
          </div>

          <div
            v-if="runStatusLabel(run)"
            class="flex items-center gap-2 text-xs text-n-slate-10"
          >
            <Icon
              :icon="
                run.status === 'awaiting_approval'
                  ? 'i-lucide-shield-alert'
                  : 'i-lucide-loader-circle'
              "
              class="w-3.5 h-3.5"
              :class="{
                'animate-spin': run.status !== 'awaiting_approval',
                'text-n-amber-11': run.status === 'awaiting_approval',
              }"
            />
            {{ runStatusLabel(run) }}
          </div>
        </template>

        <div
          v-if="
            activeRun &&
            activeRun.status !== 'awaiting_approval' &&
            !activeRun.streamingText
          "
          class="flex items-center gap-2"
        >
          <span class="text-n-iris-11 font-medium">Helix está pensando</span>
          <div class="flex gap-1">
            <div
              class="w-2 h-2 rounded-full bg-n-iris-9 animate-bounce [animation-delay:-0.3s]"
            />
            <div
              class="w-2 h-2 rounded-full bg-n-iris-9 animate-bounce [animation-delay:-0.15s]"
            />
            <div class="w-2 h-2 rounded-full bg-n-iris-9 animate-bounce" />
          </div>
        </div>
      </div>

      <div v-else class="flex-1 flex flex-col gap-6 px-2">
        <div class="flex flex-col space-y-4 py-4">
          <Icon icon="i-lucide-bot" class="text-n-slate-9 text-4xl" />
          <div class="space-y-1">
            <h3 class="text-base font-medium text-n-slate-12 leading-8">
              Helix, o técnico da LuraHosting
            </h3>
            <p class="text-sm text-n-slate-11 leading-6">
              Eu já conheço o cliente e a conversa aberta. Peça para investigar
              ou resolver e eu consulto o painel, logs e console; ações que
              alteram o ambiente pedem sua autorização antes de executar.
            </p>
          </div>
        </div>
        <div class="w-full space-y-2">
          <span class="text-xs text-n-slate-10 block">Experimente</span>
          <div class="space-y-1">
            <button
              v-for="suggestion in SUGGESTIONS"
              :key="suggestion"
              class="w-full px-3 py-2 rounded-md border border-n-weak bg-n-slate-2 text-n-slate-11 flex items-center justify-between hover:bg-n-slate-3 transition-colors"
              @click="emit('sendMessage', suggestion)"
            >
              <span>{{ suggestion }}</span>
              <Icon icon="i-lucide-chevron-right" />
            </button>
          </div>
        </div>
        <p v-if="thread && thread.error" class="text-xs text-n-ruby-11">
          {{ thread.error }}
        </p>
      </div>
    </div>

    <div class="mx-3 mt-px mb-2">
      <div class="flex items-center gap-2 justify-between w-full mb-1 px-1">
        <span class="text-xs text-n-slate-10 flex items-center gap-1">
          <Icon icon="i-lucide-bot" class="w-3.5 h-3.5" />
          Helix
        </span>
        <div class="flex items-center gap-2">
          <button
            v-tooltip.top="
              autoApprove
                ? 'Auto-aprovar LIGADO: ações de escrita rodam sem pedir. Destrutivas (excluir, reinstalar, suspender, transferir, mensagem ao cliente) continuam pedindo.'
                : 'Auto-aprovar desligado: toda ação que altera o ambiente pede sua autorização.'
            "
            class="inline-flex items-center gap-1 px-2 h-6 rounded-full text-xs border transition-colors"
            :class="
              autoApprove
                ? 'bg-n-amber-3 border-n-amber-8 text-n-amber-11'
                : 'bg-n-alpha-2 border-n-weak text-n-slate-11 hover:text-n-slate-12'
            "
            @click="emit('setAutoApprove', !autoApprove)"
          >
            <Icon
              :icon="autoApprove ? 'i-lucide-shield-check' : 'i-lucide-shield'"
              class="w-3.5 h-3.5"
            />
            Auto-aprovar: {{ autoApprove ? 'ligado' : 'desligado' }}
          </button>
          <span
            v-tooltip.top="
              thread && thread.connected ? 'Conectado' : 'Reconectando...'
            "
            class="inline-block w-2 h-2 rounded-full"
            :class="thread && thread.connected ? 'bg-n-teal-9' : 'bg-n-ruby-9'"
          />
        </div>
      </div>
      <CopilotInput
        v-if="conversationId"
        class="mb-1 w-full"
        @send="message => emit('sendMessage', message)"
      />
    </div>
  </div>
</template>
